/**
 * useOpenRouter — BYO-key OpenRouter chat completions hook.
 *
 * Canonical Analytica DSS pattern (PLAYBOOK §1, §4 Pattern A). Replaces
 * the earlier Groq-only hook with OpenRouter, giving access to 200+ models
 * (text + multimodal) through a single API key.
 *
 * The user pastes their OpenRouter API key into the <ApiKeyInput> component;
 * it's stored in localStorage under `talent-align-settings.openRouterApiKey`.
 * All chat completions go directly from the browser to OpenRouter — never
 * via any server we control.
 */

import { useCallback, useState } from "react";

const OPENROUTER_ENDPOINT = "https://openrouter.ai/api/v1/chat/completions";
const SETTINGS_KEY = "talent-align-settings";

export const OPENROUTER_MODELS = {
  // ── Frontier / SOTA ────────────────────────────────────────────────
  "openai/gpt-5.4":                      "GPT-5.4 (OpenAI Flagship)",
  "anthropic/claude-opus-4.6":           "Claude Opus 4.6 (Anthropic Flagship)",
  "google/gemini-3.1-pro-preview":       "Gemini 3.1 Pro (Google Flagship)",

  // ── Strong mid-tier ────────────────────────────────────────────────
  "anthropic/claude-sonnet-4.6":         "Claude Sonnet 4.6 (Anthropic Latest)",
  "openai/gpt-5.4-mini":                "GPT-5.4 Mini (Fast + smart)",
  "google/gemini-3-flash-preview":       "Gemini 3 Flash (Fast + capable)",
  "google/gemini-2.5-flash":             "Gemini 2.5 Flash (Cheap + fast)",
  "mistralai/mistral-large-2512":        "Mistral Large 3 (Open-weight)",
  "deepseek/deepseek-v3.2":             "DeepSeek V3.2 (Best value)",
  "qwen/qwen3.6-plus":                  "Qwen 3.6 Plus (Alibaba SOTA)",

  // ── Budget / Fast ──────────────────────────────────────────────────
  "meta-llama/llama-3.3-70b-instruct":  "Llama 3.3 70B (Recommended free)",
  "google/gemma-4-31b-it":              "Gemma 4 31B (Google open-weight, NEW)",
  "google/gemma-4-26b-a4b-it":          "Gemma 4 26B MoE (3.8B active, FREE)",
  "meta-llama/llama-3.1-8b-instruct":   "Llama 3.1 8B (Fastest)",
  "mistralai/mistral-small-2603":        "Mistral Small 4 (Cheap + capable)",
} as const;

export type OpenRouterModel = keyof typeof OPENROUTER_MODELS;

export interface OpenRouterSettings {
  openRouterApiKey?: string;
  openRouterModel?: OpenRouterModel;
  // Legacy — kept so existing users don't lose their key silently
  groqApiKey?: string;
}

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface CompleteOptions {
  messages: ChatMessage[];
  temperature?: number;
  maxTokens?: number;
  responseFormat?: "json" | "text";
  model?: OpenRouterModel;
}

export interface StreamOptions extends Omit<CompleteOptions, "responseFormat"> {
  onToken: (delta: string) => void;
  signal?: AbortSignal;
}

export interface LLMError extends Error {
  status?: number;
}

function readSettings(): OpenRouterSettings {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as OpenRouterSettings) : {};
  } catch {
    return {};
  }
}

function writeSettings(settings: Partial<OpenRouterSettings>): void {
  if (typeof window === "undefined") return;
  try {
    const existing = readSettings();
    window.localStorage.setItem(
      SETTINGS_KEY,
      JSON.stringify({ ...existing, ...settings }),
    );
  } catch {
    // localStorage can fail in private mode — non-fatal
  }
}

function getHeaders(apiKey: string): Record<string, string> {
  return {
    Authorization: `Bearer ${apiKey}`,
    "Content-Type": "application/json",
    "HTTP-Referer": "https://talentalign.dev",
    "X-Title": "TalentAlign",
  };
}

export function useOpenRouter() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = useCallback(
    async (opts: CompleteOptions): Promise<string> => {
      const settings = readSettings();
      const apiKey = settings.openRouterApiKey;
      if (!apiKey) {
        const err = "OpenRouter API key not configured. Paste your key above to get started.";
        setError(err);
        throw new Error(err);
      }

      const model = opts.model ?? settings.openRouterModel ?? "meta-llama/llama-3.3-70b-instruct";
      setLoading(true);
      setError(null);

      try {
        const body: Record<string, unknown> = {
          model,
          messages: opts.messages,
          temperature: opts.temperature ?? 0.2,
          max_tokens: opts.maxTokens ?? 2048,
        };
        if (opts.responseFormat === "json") {
          body.response_format = { type: "json_object" };
        }

        const res = await fetch(OPENROUTER_ENDPOINT, {
          method: "POST",
          headers: getHeaders(apiKey),
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errData = (await res.json().catch(() => ({}))) as {
            error?: { message?: string };
          };
          const msg = errData.error?.message ?? `OpenRouter API error: ${res.status}`;
          const err: LLMError = new Error(msg);
          err.status = res.status;
          throw err;
        }

        const data = (await res.json()) as {
          choices?: Array<{ message?: { content?: string } }>;
        };
        const content = data.choices?.[0]?.message?.content ?? "";
        return content;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error";
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const testConnection = useCallback(async (): Promise<string> => {
    const content = await complete({
      messages: [
        {
          role: "user",
          content: 'Reply with exactly the phrase "Connection successful".',
        },
      ],
      temperature: 0,
      maxTokens: 16,
    });
    return content;
  }, [complete]);

  const stream = useCallback(
    async (opts: StreamOptions): Promise<string> => {
      const settings = readSettings();
      const apiKey = settings.openRouterApiKey;
      if (!apiKey) {
        const err = "OpenRouter API key not configured.";
        setError(err);
        throw new Error(err);
      }

      const model = opts.model ?? settings.openRouterModel ?? "meta-llama/llama-3.3-70b-instruct";
      setLoading(true);
      setError(null);

      let full = "";
      try {
        const res = await fetch(OPENROUTER_ENDPOINT, {
          method: "POST",
          headers: getHeaders(apiKey),
          signal: opts.signal,
          body: JSON.stringify({
            model,
            messages: opts.messages,
            temperature: opts.temperature ?? 0.2,
            max_tokens: opts.maxTokens ?? 2048,
            stream: true,
          }),
        });

        if (!res.ok || !res.body) {
          const errData = (await res.json().catch(() => ({}))) as {
            error?: { message?: string };
          };
          const msg = errData.error?.message ?? `OpenRouter stream error: ${res.status}`;
          const err: LLMError = new Error(msg);
          err.status = res.status;
          throw err;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";
          for (const event of events) {
            const line = event.trim();
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            if (payload === "[DONE]") return full;
            try {
              const data = JSON.parse(payload) as {
                choices?: Array<{ delta?: { content?: string } }>;
              };
              const delta = data.choices?.[0]?.delta?.content;
              if (delta) {
                full += delta;
                opts.onToken(delta);
              }
            } catch {
              // Skip malformed SSE rows
            }
          }
        }
        return full;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return full;
        const msg = err instanceof Error ? err.message : "Unknown error";
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    complete,
    stream,
    testConnection,
    loading,
    error,
    setError,
    readSettings,
    writeSettings,
  };
}
